import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { Roles, ZoomLevels } from 'enums';
import AdminSubpageHeader from 'features/admin/admin-subpage-header';
import StatusMessageDialog from 'features/admin/status-message/status-message-dialog';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiLink from 'features/ui/base-components/pai-link';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import { StatusMessage, deleteStatusMessage, getAllStatusMessages, updateStatusMessage } from '../API/status-message';
import { useErrorHandler } from '../features/admin/status-message/status-message-utils';
import { useUserData } from '../hooks/use-user-data';
import DeleteStatusMessageDialog from 'features/admin/status-message/delete-status-message-dialog';
import useErrorStore from 'stores/error-store';

const CollapsibleText: React.FC<{ text: string; maxLength: number }> = ({ text, maxLength = 100 }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setIsExpanded(!isExpanded);
  };
  const displayedText = isExpanded ? text : text.substring(0, maxLength) + (text.length > maxLength ? '...' : '');
  return (
    <div>
      {displayedText}
      {text.length > maxLength && (
        <PaiLink
          text={isExpanded ? t('less') : t('more')}
          inline={!isExpanded}
          ml={!isExpanded ? '4px' : undefined}
          size={'md'}
          variant={'primary'}
          underline={'hover'}
          action={toggleExpand}
        />
      )}
    </div>
  );
};

const StatusMessageManagement: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { token, role } = useUserData();
  const { handleError } = useErrorHandler();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [statusMessages, setStatusMessages] = useState<StatusMessage[]>([]);
  const [statusMessagesLoading, setStatusMessagesLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentMessage, setCurrentMessage] = useState<StatusMessage | null>(null);
  const [isNewMessage, setIsNewMessage] = useState(false);

  const [deleteStatusMessageDialog, setDeleteStatusMessageDialog] = useState<{ open: boolean; loading: boolean; messageId: string | null }>({
    open: false,
    loading: false,
    messageId: null,
  });

  const fetchStatusMessages = async () => {
    if (!token) return;

    try {
      setStatusMessagesLoading(true);
      const response = await getAllStatusMessages();
      if (response.data) {
        setStatusMessages(response.data);
      } else if (response.error) {
        handleError(response.error, 'Failed to fetch status messages');
      }
    } catch (error) {
      console.error('Failed to fetch status messages', error);
      handleError(error, 'Failed to fetch status messages');
    }
    setStatusMessagesLoading(false);
  };

  useEffect(() => {
    fetchStatusMessages();
  }, [token]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleActive = async (message: StatusMessage) => {
    if (!token) return;

    try {
      // Note: We use isActive in the parameter (internal naming convention)
      // but the API expects 'active' (which is handled by the API function)
      const response = await updateStatusMessage(message.id, {
        isActive: !message.active,
      });

      if (response.data) {
        // Update the message in the local state
        setStatusMessages((prevMessages) => prevMessages.map((msg) => (msg.id === message.id ? { ...msg, active: !msg.active } : msg)));
      } else if (response.error) {
        handleError(response.error, 'Failed to update status message');
      }
    } catch (error) {
      console.error('Failed to update status message', error);
      handleError(error, 'Failed to update status message');
    }
  };

  const handleEditMessage = (message: StatusMessage) => {
    setCurrentMessage(message);
    setIsNewMessage(false);
    setDialogOpen(true);
  };

  const handleAddMessage = () => {
    setCurrentMessage(null);
    setIsNewMessage(true);
    setDialogOpen(true);
  };

  const handleDialogClose = (refreshNeeded: boolean) => {
    setDialogOpen(false);
    if (refreshNeeded) {
      fetchStatusMessages();
    }
  };

  /**
   * Delete Status Message Dialog
   */

  const handleOpenDeleteStatusMessageDialog = (messageId: string) => {
    return () => {
      setDeleteStatusMessageDialog((prev) => ({ ...prev, open: true, messageId }));
    };
  };

  const handleCloseDeleteStatusMessageDialog = () => {
    setDeleteStatusMessageDialog({ open: false, loading: false, messageId: null });
  };

  const handleDeleteStatusMessage = async () => {
    if (!deleteStatusMessageDialog.messageId) return;

    setDeleteStatusMessageDialog((prev) => ({ ...prev, loading: true }));

    const res = await deleteStatusMessage(deleteStatusMessageDialog.messageId);

    if (!res.error) {
      handleCloseDeleteStatusMessageDialog();
      setStatusMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== deleteStatusMessageDialog.messageId));
    } else if (res.error) {
      setDeleteStatusMessageDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error, '/statusMessage/:id', 'delete');
    }
  };

  if (role !== Roles.OWNER) {
    return <Typography p={4}>{t('noPermission', 'You do not have permission to access this page.')}</Typography>;
  }

  return (
    <div className={classes.container}>
      <div className={classes.tabContainer}>
        <Stack spacing={2}>
          <AdminSubpageHeader title={t('statusMessages')} />
          <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
            <PaiButton text={t('addNewMessage')} startIcon={<AddIcon />} size={'medium'} border={'rounded'} action={handleAddMessage} />
          </Stack>
        </Stack>
      </div>
      <div className={classes.content}>
        {statusMessagesLoading ? (
          <PaiDotLoader />
        ) : (
          <TableContainer className={classes.table}>
            <Table size={'small'} stickyHeader={true}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">{t('active')}</TableCell>
                  <TableCell style={{ width: '28%' }}>{t('englishText')}</TableCell>
                  <TableCell style={{ width: '28%' }}>{t('germanText')}</TableCell>
                  <TableCell style={{ width: '16%' }}>{t('createdAt')}</TableCell>
                  <TableCell style={{ width: '16%' }}>{t('updatedAt')}</TableCell>
                  <TableCell style={{ width: '20%' }}>{t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statusMessages.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((message) => (
                  <TableRow hover key={message.id}>
                    <TableCell padding="checkbox">
                      <PaiCheckbox checked={message.active} onChange={() => handleToggleActive(message)} />
                    </TableCell>
                    <TableCell>
                      <CollapsibleText text={message.englishText} maxLength={100} />
                    </TableCell>
                    <TableCell>
                      <CollapsibleText text={message.germanText} maxLength={100} />
                    </TableCell>
                    <TableCell>{formatDateTime(new Date(message.createdAt).toISOString(), 'Pp')}</TableCell>
                    <TableCell>{formatDateTime(new Date(message.updatedAt).toISOString(), 'Pp')}</TableCell>
                    <TableCell>
                      <Stack direction={'row'} alignItems={'center'} gap={1}>
                        <IconButton className={classes.iconButton} onClick={() => handleEditMessage(message)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton className={classes.iconButton} onClick={handleOpenDeleteStatusMessageDialog(message.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {statusMessages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t('noStatusMessagesFound')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={statusMessages.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}
      </div>
      <StatusMessageDialog open={dialogOpen} onClose={handleDialogClose} message={currentMessage} isNew={isNewMessage} />
      <DeleteStatusMessageDialog
        open={deleteStatusMessageDialog.open}
        handleClose={handleCloseDeleteStatusMessageDialog}
        handleDelete={handleDeleteStatusMessage}
      />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  tabContainer: {
    padding: '16px 32px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  content: {
    height: '100%',
    padding: '16px 32px',
    overflow: 'auto',
    display: 'flex',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'nowrap',
  },
  table: {
    borderRadius: '6px',
    '& th, & td': {
      fontSize: theme.typography.pxToRem(15),
      padding: '8px 16px',
      backgroundColor: '#FFF',
    },
    '& tr:last-child td': {
      borderBottom: 'none',
    },
  },
  iconButton: {
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
    },
  },
}));

export default StatusMessageManagement;
